import { gql } from '@apollo/client';
import { visit } from 'graphql';

/**
 * Fields the Offer Comp page ALWAYS needs, independent of which data-table-profile columns are
 * visible: structural ids plus the dependencies of client-derived values computed in
 * offer-comparison-table.tsx (offerListingCount adjustment, highestOfferTotalBidPrice /
 * highestOfferPercentSuggestedPrice, and offer-shift matching).
 *
 * Kept as a named fragment so the non-configurable part of the query is a single, greppable source
 * of truth. The dynamic query builder (build-segmented-listings-query.js) spreads this fragment and
 * layers the profile-driven column selections on top.
 */
export const OfferCompRequiredFields = gql`
  fragment OfferCompRequiredFields on Inventory {
    _id
    offerListingCount
    buyerSiteNames # buyer filter + buyer list (offer-comparison-progressive.js)
    # --- fields read by the row filters (offer-comparison-filter-operations.ts) ---
    availableQuantity # status filter + onHand comparison
    onHandQuantity # status/partially-awarded filter + final "> 0" guard
    foodType # category filter
    foodSubtype # category filter
    truckType # truck type filter + polling details
    businessUnit # business unit filter
    tags # tied filter
    inventoryType # inventory type filter
    bestByDate # code date filter
    importBatch # import batch filter
    distributionLists {
      listId # distribution list filter
    }
    # --- selection / navigation / polling (offer-comparison-progressive.js) ---
    siteId # polling details origin
    description # selected inventory description
    site {
      _id # site filter
      itemImportSite
    }
    offerListings {
      _id # offer-shift matching
      inventoryId # offer-shift matching
      totalPrice # highestOfferTotalBidPrice + highest-offer sort
      quantity # highestOfferPercentSuggestedPrice
      suggestedUnitPrice # highestOfferPercentSuggestedPrice
      status # trucklane/post-award/suggested-award/canAwardSuggested filters
      logisticsTerm # logistics filter + trucklane cache invalidation
      tags # below-reserve filter
      sellerLocationId # trucklane cache invalidation (recalc)
      buyerDestinationId # trucklane cache invalidation (recalc)
      transactionCycleId # trucklane cache invalidation (recalc)
      pricingUOM # unit price display
      pricingDivisor # unit price display
      negotiation {
        _id # keeps Negotiation normalized — other queries (e.g. trucklaneViewQuery) select negotiation { _id }, and an id-less write here would be clobbered by theirs, breaking this query's cache reads
        status # negotiation status filter
      }
      postAwardUpdates {
        status # post-award-updates filter
      }
      suggestions {
        award {
          suggested # suggested-award filter + canAwardSuggested
        }
      }
    }
  }
`;

/**
 * Machine-readable view of the fragment above, as row-relative field paths to its leaf fields
 * (nested selections are flattened, e.g. `offerListings.negotiation.status`). Derived from the
 * fragment's AST at module load so it can never drift; the query builder uses it to avoid
 * re-selecting fields the fragment already covers.
 */
export const REQUIRED_PATHS: string[] = (() => {
  const paths: string[] = [];
  const stack: string[] = [];
  visit(OfferCompRequiredFields, {
    Field: {
      enter(node) {
        stack.push(node.name.value);
        if (!node.selectionSet) paths.push(stack.join('.'));
      },
      leave() {
        stack.pop();
      },
    },
  });
  return paths;
})();
